/**
 * Browser Compatibility Utilities
 * Handles cross-browser compatibility issues for the initialization flow
 */

/**
 * Detect the current browser
 */
export const detectBrowser = () => {
  const userAgent = navigator.userAgent.toLowerCase();
  
  if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
    return 'chrome';
  } else if (userAgent.includes('firefox')) {
    return 'firefox';
  } else if (userAgent.includes('safari') && !userAgent.includes('chrome')) {
    return 'safari';
  } else if (userAgent.includes('edg')) {
    return 'edge';
  } else {
    return 'unknown';
  }
};

/**
 * Get browser-specific timeout adjustments
 * Some browsers handle timeouts differently, especially on slower devices
 */
export const getBrowserTimeoutAdjustment = () => {
  const browser = detectBrowser();
  
  switch (browser) {
    case 'safari':
      // Safari on iOS can be slower with network requests
      return 1.2; // 20% longer timeout
    case 'firefox':
      // Firefox generally handles timeouts well
      return 1.0;
    case 'edge':
      // Edge can be slower on older versions
      return 1.1; // 10% longer timeout
    case 'chrome':
    default:
      return 1.0; // Standard timeout
  }
};

/**
 * Check if the browser supports modern features we use
 */
export const checkBrowserSupport = () => {
  const support = {
    fetch: typeof fetch !== 'undefined',
    promises: typeof Promise !== 'undefined',
    asyncAwait: (async () => {})().constructor === Promise,
    intersectionObserver: typeof IntersectionObserver !== 'undefined',
    requestAnimationFrame: typeof requestAnimationFrame !== 'undefined',
    localStorage: (() => {
      try {
        const test = '__test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    })(),
    sessionStorage: (() => {
      try {
        const test = '__test__';
        sessionStorage.setItem(test, test);
        sessionStorage.removeItem(test);
        return true;
      } catch (e) {
        return false;
      }
    })(),
    webWorkers: typeof Worker !== 'undefined',
    serviceWorkers: 'serviceWorker' in navigator,
    ariaLive: (() => {
      const div = document.createElement('div');
      div.setAttribute('aria-live', 'polite');
      return div.getAttribute('aria-live') === 'polite';
    })()
  };

  return support;
};

/**
 * Get browser-specific CSS classes for styling adjustments
 */
export const getBrowserClasses = () => {
  const browser = detectBrowser();
  const classes = [`browser-${browser}`];
  
  // Add mobile detection
  if (/android|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(navigator.userAgent)) {
    classes.push('mobile-browser');
  }
  
  // Add touch support detection
  if ('ontouchstart' in window || navigator.maxTouchPoints > 0) {
    classes.push('touch-enabled');
  }
  
  return classes;
};

/**
 * Handle browser-specific focus management
 */
export const focusElement = (element, options = {}) => {
  if (!element) return false;
  
  const browser = detectBrowser();
  const { preventScroll = false, delay = 0 } = options;
  
  const doFocus = () => {
    try {
      if (browser === 'safari' && preventScroll) {
        // Safari has issues with preventScroll option
        const scrollX = window.scrollX;
        const scrollY = window.scrollY;
        element.focus();
        window.scrollTo(scrollX, scrollY);
      } else {
        element.focus({ preventScroll });
      }
      return true;
    } catch (error) {
      console.warn('Focus failed:', error);
      return false;
    }
  };
  
  if (delay > 0) {
    setTimeout(doFocus, delay);
  } else {
    return doFocus();
  }
};

/**
 * Handle browser-specific scroll behavior
 */
export const scrollToElement = (element, options = {}) => {
  if (!element) return;
  
  const browser = detectBrowser();
  const { behavior = 'smooth', block = 'center' } = options;
  
  try {
    if (browser === 'safari' && behavior === 'smooth') {
      // Safari has inconsistent smooth scrolling
      element.scrollIntoView({ behavior: 'auto', block });
    } else {
      element.scrollIntoView({ behavior, block });
    }
  } catch (error) {
    // Fallback for older browsers
    element.scrollIntoView();
  }
};

/**
 * Create browser-compatible event listeners
 */
export const addEventListenerCompat = (element, event, handler, options = {}) => {
  if (!element || !event || !handler) return null;
  
  const browser = detectBrowser();
  
  // Handle passive event listeners for better performance
  if (event === 'touchstart' || event === 'touchmove' || event === 'wheel') {
    options = { ...options, passive: true };
  }
  
  try {
    element.addEventListener(event, handler, options);
    return () => element.removeEventListener(event, handler, options);
  } catch (error) {
    // Fallback for older browsers that don't support options object
    element.addEventListener(event, handler);
    return () => element.removeEventListener(event, handler);
  }
};

/**
 * Handle browser-specific animation frame requests
 */
export const requestAnimationFrameCompat = (callback) => {
  if (typeof requestAnimationFrame !== 'undefined') {
    return requestAnimationFrame(callback);
  } else {
    // Fallback for older browsers
    return setTimeout(callback, 16); // ~60fps
  }
};

/**
 * Cancel animation frame with browser compatibility
 */
export const cancelAnimationFrameCompat = (id) => {
  if (typeof cancelAnimationFrame !== 'undefined') {
    cancelAnimationFrame(id);
  } else {
    clearTimeout(id);
  }
};

/**
 * Handle browser-specific storage operations
 */
export const storageCompat = {
  setItem: (key, value, type = 'localStorage') => {
    try {
      const storage = type === 'sessionStorage' ? sessionStorage : localStorage;
      storage.setItem(key, JSON.stringify(value));
      return true;
    } catch (error) {
      console.warn(`${type} not available:`, error);
      return false;
    }
  },
  
  getItem: (key, type = 'localStorage') => {
    try {
      const storage = type === 'sessionStorage' ? sessionStorage : localStorage;
      const item = storage.getItem(key);
      return item ? JSON.parse(item) : null;
    } catch (error) {
      console.warn(`${type} not available:`, error);
      return null;
    }
  },
  
  removeItem: (key, type = 'localStorage') => {
    try {
      const storage = type === 'sessionStorage' ? sessionStorage : localStorage;
      storage.removeItem(key);
      return true;
    } catch (error) {
      console.warn(`${type} not available:`, error);
      return false;
    }
  }
};

/**
 * Handle browser-specific clipboard operations
 */
export const clipboardCompat = {
  writeText: async (text) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        return true;
      } else {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = text;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        const result = document.execCommand('copy');
        document.body.removeChild(textArea);
        return result;
      }
    } catch (error) {
      console.warn('Clipboard write failed:', error);
      return false;
    }
  }
};

/**
 * Get browser-specific performance optimizations
 */
export const getPerformanceOptimizations = () => {
  const browser = detectBrowser();
  
  return {
    // Reduce animations on Safari for better performance
    reduceAnimations: browser === 'safari',
    
    // Use different loading strategies based on browser
    useIntersectionObserver: typeof IntersectionObserver !== 'undefined',
    
    // Adjust debounce timing based on browser performance
    debounceDelay: browser === 'safari' ? 300 : 200,
    
    // Use different caching strategies
    useServiceWorker: 'serviceWorker' in navigator && browser !== 'safari',
    
    // Adjust batch sizes for DOM operations
    domBatchSize: browser === 'firefox' ? 50 : 100
  };
};

/**
 * Initialize browser compatibility features
 */
export const initializeBrowserCompatibility = () => {
  const browser = detectBrowser();
  const support = checkBrowserSupport();
  const classes = getBrowserClasses();
  
  // Add browser classes to document
  document.documentElement.classList.add(...classes);
  
  // Log browser information for debugging
  console.log('Browser compatibility initialized:', {
    browser,
    support,
    classes,
    userAgent: navigator.userAgent
  });
  
  // Warn about unsupported features
  const unsupportedFeatures = Object.entries(support)
    .filter(([, supported]) => !supported)
    .map(([feature]) => feature);
    
  if (unsupportedFeatures.length > 0) {
    console.warn('Unsupported browser features detected:', unsupportedFeatures);
  }
  
  return {
    browser,
    support,
    classes,
    unsupportedFeatures
  };
};

export default {
  detectBrowser,
  getBrowserTimeoutAdjustment,
  checkBrowserSupport,
  getBrowserClasses,
  focusElement,
  scrollToElement,
  addEventListenerCompat,
  requestAnimationFrameCompat,
  cancelAnimationFrameCompat,
  storageCompat,
  clipboardCompat,
  getPerformanceOptimizations,
  initializeBrowserCompatibility
};
